import { BarChart3, PieChart } from "lucide-react";
import {
  PieChart as RechartsPieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import formatCurrency from "@/utils/formatCurrency";

// Stable chart configuration (module-level to avoid re-creating on each render)
const BAR_TOP_RADIUS = [4, 4, 0, 0];

const FinancialCharts = ({ incomes, expenses, savings }) => {
  const totalIncome = incomes.reduce((sum, i) => sum + (i.is_monthly ? i.monthly_amount * 12 : i.yearly_amount), 0);
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.is_monthly ? e.monthly_amount * 12 : e.yearly_amount), 0);
  const totalSavings = savings.reduce((sum, s) => sum + (s.is_monthly ? s.monthly_amount * 12 : s.yearly_amount), 0);
  const surplus = totalIncome - totalExpenses - totalSavings;

  const incomeData = incomes
    .filter(i => (i.is_monthly ? i.monthly_amount : i.yearly_amount / 12) > 0)
    .map(i => ({ name: i.category, value: i.is_monthly ? i.monthly_amount * 12 : i.yearly_amount }));

  const expenseByCategory = {};
  expenses.forEach(e => {
    const value = e.is_monthly ? e.monthly_amount * 12 : e.yearly_amount;
    expenseByCategory[e.category] = (expenseByCategory[e.category] || 0) + value;
  });
  const expenseData = Object.entries(expenseByCategory)
    .filter(([_, value]) => value > 0)
    .map(([name, value]) => ({ name, value }));

  const cashFlowData = [
    { name: "Income", value: totalIncome, fill: "#22c55e" },
    { name: "Expenses", value: totalExpenses, fill: "#ef4444" },
    { name: "Savings", value: totalSavings, fill: "#3b82f6" },
    { name: "Surplus", value: Math.max(0, surplus), fill: "#14b8a6" }
  ];

  const monthlyData = [
    { month: "Monthly", income: totalIncome / 12, expenses: totalExpenses / 12, savings: totalSavings / 12 }
  ];

  const COLORS = ["#14b8a6", "#22c55e", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6"];

  if (totalIncome === 0 && totalExpenses === 0 && totalSavings === 0) {
    return (
      <div className="finance-card text-center py-12" data-testid="charts-empty">
        <BarChart3 size={48} className="mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-semibold text-gray-500">No Data to Display</h3>
        <p className="text-gray-400 mt-2">Enter your income, expenses, and savings to see visual analytics</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="financial-charts">
      <h2 className="section-title flex items-center gap-2">
        <PieChart size={24} className="text-teal-600" />
        Financial Analytics
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="finance-card">
          <h3 className="text-lg font-semibold mb-4">Cash Flow Overview (Yearly)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={cashFlowData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
              <Tooltip formatter={(v) => `₹${formatCurrency(v)}`} />
              <Bar dataKey="value" radius={BAR_TOP_RADIUS}>
                {cashFlowData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {incomeData.length > 0 && (
          <div className="finance-card">
            <h3 className="text-lg font-semibold mb-4">Income Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <RechartsPieChart>
                <Pie data={incomeData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`} outerRadius={80} dataKey="value">
                  {incomeData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                </Pie>
                <Tooltip formatter={(v) => `₹${formatCurrency(v)}`} />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        )}

        {expenseData.length > 0 && (
          <div className="finance-card">
            <h3 className="text-lg font-semibold mb-4">Expense Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <RechartsPieChart>
                <Pie data={expenseData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`} outerRadius={80} dataKey="value">
                  {expenseData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                </Pie>
                <Tooltip formatter={(v) => `₹${formatCurrency(v)}`} />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="finance-card">
          <h3 className="text-lg font-semibold mb-4">Monthly Breakdown</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
              <Tooltip formatter={(v) => `₹${formatCurrency(v)}`} />
              <Legend />
              <Bar dataKey="income" name="Income" fill="#22c55e" radius={BAR_TOP_RADIUS} />
              <Bar dataKey="expenses" name="Expenses" fill="#ef4444" radius={BAR_TOP_RADIUS} />
              <Bar dataKey="savings" name="Savings" fill="#3b82f6" radius={BAR_TOP_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default FinancialCharts;
